import { Pagination } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { User } from "../../types/Types";
import { resolvedObjectId } from "../../utils/connectionUserUtils";
import type { ConnectionTarget } from "../../utils/connectionUserUtils";
import EventAttendeeCard, { type AttendeeWithState } from "./EventAttendeeCard";
import { ACTIONS_COL_WIDTH, COL, GAP, colStyle } from "./eventAttendeeCardShared";
import { normalizedEmail, PAGE_SIZE } from "./eventAttendeesHelpers";

export interface EventAttendeesListProps {
  loading: boolean;
  filteredCount: number;
  paginatedList: AttendeeWithState[];
  currentPage: number;
  totalPages: number;
  showPagination: boolean;
  onPageChange: (page: number | ((prev: number) => number)) => void;
  viewerOidNorm: string;
  viewerOidDuplicateCount: number;
  currentUser: User | null | undefined;
  noteObjectIdsForEvent: Set<string>;
  selectedAttendeeKeys: Set<string>;
  currentUserMembership: ConnectionTarget | string | undefined;
  explorerLimitReached: boolean;
  isExplorerUser: boolean;
  onBlockedConnectionAction: () => boolean | void;
  onViewProfile: (user: User) => void;
  onSaveNote: (user: User) => void;
  onMessage: (id: number) => void;
  onConnect: (user: User) => void;
  onCancelRequest: (id: number) => void;
  onAccept: (id: number, user: User) => void;
  onIgnore: (id: number) => void;
  onRemoveConnection: (user: User) => void;
  sendConnectionId: number | null;
  cancellingId: number | null;
  onToggleAttendeeSelect: (key: string) => void;
}

function EventAttendeesList({
  loading,
  filteredCount,
  paginatedList,
  currentPage,
  totalPages,
  showPagination,
  onPageChange,
  viewerOidNorm,
  viewerOidDuplicateCount,
  currentUser,
  noteObjectIdsForEvent,
  selectedAttendeeKeys,
  currentUserMembership,
  explorerLimitReached,
  isExplorerUser,
  onBlockedConnectionAction,
  onViewProfile,
  onSaveNote,
  onMessage,
  onConnect,
  onCancelRequest,
  onAccept,
  onIgnore,
  onRemoveConnection,
  sendConnectionId,
  cancellingId,
  onToggleAttendeeSelect,
}: EventAttendeesListProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex-grow-1">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("feed.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (filteredCount === 0) {
    return (
      <div className="flex-grow-1">
        <div className="bg-light rounded p-5 text-center">
          <div className="mb-3 display-4 opacity-75">👋</div>
          <h6 className="fw-semibold mb-2">{t("events.noAttendeesFound")}</h6>
          <p className="text-muted small mb-0">{t("events.noAttendeesHint")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow-1">
      <div className="d-flex flex-column" style={{ gap: 12 }}>
        <div className="px-3 py-2 bg-light d-none d-lg-block border rounded-3">
          <div
            className="d-flex align-items-center flex-nowrap w-100 min-w-0 text-muted small fw-semibold"
            style={{ gap: GAP }}
            role="row"
          >
            <div
              className="d-flex align-items-center flex-shrink-0"
              style={{ gap: 4, minWidth: COL.pin + COL.index }}
            >
              <span
                className="flex-shrink-0"
                style={{ width: COL.pin }}
                aria-hidden
              />
              <span
                className="flex-shrink-0 text-end"
                style={{ width: COL.index }}
                role="columnheader"
              >
                No.
              </span>
            </div>
            <span
              className="flex-shrink-0"
              style={{ width: COL.avatar }}
              aria-hidden
            />
            <div className="min-w-0" style={colStyle(COL.name, 1.1)} role="columnheader">
              Name
            </div>
            <div className="min-w-0" style={colStyle(COL.city)} role="columnheader">
              City
            </div>
            <div className="min-w-0" style={colStyle(COL.company)} role="columnheader">
              Company
            </div>
            <div className="min-w-0" style={colStyle(COL.sector)} role="columnheader">
              Sector
            </div>
            <div
              className="min-w-0"
              style={colStyle(COL.objective, 1.2)}
              role="columnheader"
            >
              Objective
            </div>
            <div className="min-w-0" style={colStyle(COL.membership)} role="columnheader">
              Membership Type
            </div>
            <div
              className="flex-shrink-0 text-center"
              style={{ width: ACTIONS_COL_WIDTH }}
              role="columnheader"
            >
              Actions
            </div>
          </div>
        </div>
        {paginatedList.map((attendee, rowIndex) => {
          const attendeeOid = resolvedObjectId(attendee.user).toLowerCase();
          const viewerNumericId = currentUser?.id ?? 0;
          const viewerEmail = normalizedEmail(currentUser);
          const attendeeEmail = normalizedEmail(attendee.user);
          const idMatchesViewer =
            viewerNumericId > 0 &&
            attendee.user.id > 0 &&
            attendee.user.id === viewerNumericId;
          const emailMatchesViewer =
            !!viewerEmail && !!attendeeEmail && attendeeEmail === viewerEmail;
          const oidMatchesViewer =
            !!viewerOidNorm && !!attendeeOid && attendeeOid === viewerOidNorm;
          const isCurrentUser =
            idMatchesViewer ||
            emailMatchesViewer ||
            (oidMatchesViewer && viewerOidDuplicateCount <= 1);
          const hasNote = noteObjectIdsForEvent.has(attendeeOid);
          const selectKey = `${attendeeOid || "no-oid"}-${attendee.user.id}`;
          const listKey = `${selectKey}-${(currentPage - 1) * PAGE_SIZE + rowIndex}`;
          return (
            <EventAttendeeCard
              key={listKey}
              attendee={attendee}
              sequentialNumber={(currentPage - 1) * PAGE_SIZE + rowIndex + 1}
              isCurrentUser={isCurrentUser}
              currentUserMembership={currentUserMembership}
              explorerLimitReached={explorerLimitReached}
              isExplorerUser={isExplorerUser}
              onBlockedConnectionAction={onBlockedConnectionAction}
              onViewProfile={onViewProfile}
              onSaveNote={onSaveNote}
              onMessage={onMessage}
              onConnect={onConnect}
              onCancelRequest={onCancelRequest}
              onAccept={onAccept}
              onIgnore={onIgnore}
              onRemoveConnection={onRemoveConnection}
              sendConnectionId={sendConnectionId}
              cancellingId={cancellingId}
              hasNote={hasNote}
              isSelected={selectedAttendeeKeys.has(selectKey)}
              onSelect={() => onToggleAttendeeSelect(selectKey)}
            />
          );
        })}
      </div>
      {showPagination && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => onPageChange((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => onPageChange(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => onPageChange((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
}

export default EventAttendeesList;
